import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

/** --- Constants --- */
const REQUIRED_FIELDS = [
    "fullName",
    "companyName",
    "phoneNumber",
    "email",
    "productName",
    "city",
];

const QuoteForm = () => {
    const [form, setForm] = useState({
        fullName: "",
        companyName: "",
        phoneNumber: "",
        email: "",
        productName: "",
        city: "",
        message: "",
        attachment: null,
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [formMessage, setFormMessage] = useState("");
    const [formMessageType, setFormMessageType] = useState(""); // "success" | "error"
    const [API_URL, setAPI_URL] = useState(""); // loaded from config.json

    const fileInputRef = useRef(null);

    /** --- Load API URL from config.json --- */
    useEffect(() => {
        fetch("/config.json")
            .then((res) => res.json())
            .then((data) => setAPI_URL(`${data.API_BASE_URL}/service/getAQuote`))
            .catch((err) => console.error("Failed to load config.json", err));
    }, []);

    /** --- Validators --- */
    const validators = {
        fullName: (v) => (v.trim() ? null : "Full name is required."),
        companyName: (v) => (v.trim() ? null : "Company name is required."),
        phoneNumber: (v) =>
            /^\d{10}$/.test(v) ? null : "Phone number must be 10 digits.",
        email: (v) =>
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
                ? null
                : "Enter a valid email address.",
        productName: (v) => (v.trim() ? null : "Product name is required."),
        city: (v) => (v.trim() ? null : "City is required."),
        attachment: (file) => {
            if (!file) return null;
            const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
            if (!allowedTypes.includes(file.type))
                return "Only JPG, JPEG, PNG, or PDF files are allowed.";

            const maxImageSize = 3 * 1024 * 1024; // 3MB
            const maxPdfSize = 5 * 1024 * 1024; // 5MB

            if (file.type === "application/pdf" && file.size > maxPdfSize)
                return "PDF must be under 5MB.";
            if (file.type.startsWith("image/") && file.size > maxImageSize)
                return "Image must be under 3MB.";

            return null;
        },
    };

    /** --- Track form validity --- */
    useEffect(() => {
        const hasEmpty = REQUIRED_FIELDS.some((field) => !form[field]);
        const hasError = Object.values(errors).some(Boolean);
        setIsFormValid(!hasEmpty && !hasError);
    }, [form, errors]);

    /** --- Validate Field / Form --- */
    const validateField = (name, value) => {
        if (!validators[name]) return;
        const error = validators[name](value);
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const validateForm = () => {
        const newErrors = {};
        REQUIRED_FIELDS.forEach((field) => {
            newErrors[field] = validators[field](form[field]);
        });
        newErrors.attachment = validators.attachment(form.attachment);
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    /** --- Input Handlers --- */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] || null;
        setForm((prev) => ({ ...prev, attachment: file }));
        validateField("attachment", file);
    };

    /** --- Reset Form --- */
    const resetForm = () => {
        setForm({
            fullName: "",
            companyName: "",
            phoneNumber: "",
            email: "",
            productName: "",
            city: "",
            message: "",
            attachment: null,
        });
        setErrors({});
        setUploadProgress(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    /** --- Show message for 5 seconds --- */
    const showMessage = (msg, type) => {
        setFormMessage(msg);
        setFormMessageType(type);
        setTimeout(() => setFormMessage(""), 5000);
    };

    /** --- Submit --- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting || !API_URL) return;

        if (!validateForm()) {
            showMessage("Please fix the highlighted fields.", "error");
            return;
        }

        setIsSubmitting(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            Object.entries(form).forEach(([key, value]) => {
                if (value !== null) formData.append(key, value);
            });
            formData.append("type", "getAQuote");

            const response = await axios.post(API_URL, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percent);
                    }
                },
            });

            resetForm();
            showMessage(response.data?.message || "Quote submitted successfully!", "success");
        } catch (err) {
            if (err.response)
                showMessage(err.response.data?.message || "Server error occurred.", "error");
            else if (err.request)
                showMessage("Network error. Please check your connection.", "error");
            else showMessage(err.message || "An unexpected error occurred.", "error");
        } finally {
            setIsSubmitting(false);
            setUploadProgress(null);
        }
    };

    /** --- Render --- */
    return (
        <form onSubmit={handleSubmit} className="container p-3">
            <h5 className="text-center mb-4" style={{ color: "var(--brand-color)" }}>
                Fill the form below to get a free quote
            </h5>

            {formMessage && (
                <div
                    className={`alert text-center ${
                        formMessageType === "success" ? "alert-success" : "alert-danger"
                    }`}
                >
                    {formMessage}
                </div>
            )}

            {/* Fields */}
            <div className="row g-3">
                {["fullName", "companyName", "phoneNumber", "email", "productName", "city"].map(
                    (field) => (
                        <div className="col-md-6 mt-2" key={field}>
                            <input
                                type={field === "email" ? "email" : "text"}
                                name={field}
                                placeholder={`${field.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())} *`}
                                value={form[field]}
                                onChange={handleChange}
                                onBlur={(e) => validateField(field, e.target.value)}
                                className={`form-control ${errors[field] ? "is-invalid" : ""}`}
                                required
                            />
                            {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
                        </div>
                    )
                )}
            </div>

            {/* Message */}
            <div className="mt-2">
                <textarea
                    name="message"
                    rows="3"
                    placeholder="Your Message (optional)"
                    value={form.message}
                    onChange={handleChange}
                    className="form-control"
                />
            </div>

            {/* File Upload */}
            <div className="mt-2 d-flex align-items-center justify-content-center gap-2">
                <label className="form-label m-0" style={{ whiteSpace: "nowrap" }}>
                    Upload File
                </label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className={`form-control ${errors.attachment ? "is-invalid" : ""}`}
                    style={{ flex: 1, maxWidth: "300px" }}
                />
            </div>

            {errors.attachment && (
                <div className="invalid-feedback d-block">{errors.attachment}</div>
            )}

            {form.attachment && !errors.attachment && (
                <div className="mt-1 d-flex align-items-center justify-content-center text-secondary small">
                    Selected: {form.attachment.name} (
                    {(form.attachment.size / (1024 * 1024)).toFixed(2)} MB)
                </div>
            )}

            {uploadProgress !== null && (
                <div className="progress mt-2">
                    <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                    />
                </div>
            )}

            {/* Submit */}
            <div className="text-center mt-4">
                <button
                    type="submit"
                    className="btn btn-brand"
                    disabled={!isFormValid || isSubmitting || !API_URL}
                >
                    {isSubmitting ? "Submitting..." : "Submit"}
                </button>
            </div>
        </form>
    );
};

export default QuoteForm;
